import {useParams} from 'react-router-dom';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import capitalize from 'lodash/capitalize';

import {EventLink} from '@entities/event';
import {
  formatOrderEnumValue,
  OrderFullInfo,
  OrderLabelBadge,
  useGetOrderFullInfo,
} from '@entities/order';
import {
  CardBox,
  Item,
  NumberValue,
  PrimaryLabel,
  SecondaryLabel,
  StringValue,
} from '@shared/components';
import {DATETIME_FORMAT} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {OrderItemsTable} from './OrderItemsTable';
import {OrderTransactionsTab} from './OrderTransactionsTab';

import {COLOR__WHITE} from '@/theme/colors';

const cardBoxCommonProps = {
  bgcolor: COLOR__WHITE,
  width: 'fit-content',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
} as const;

type OrderTab = 'items' | 'transactions';

const tabs: {value: OrderTab; label: string}[] = [
  {value: 'items', label: 'Items'},
  {value: 'transactions', label: 'Transactions'},
];

export const OrderFullInfoPage = () => {
  const {id: orderId} = useParams<{id: string}>();
  const {urlState, setUrl} = useUrlState<{tab: OrderTab}>({tab: 'items'});

  const {data, isPending, error} = useGetOrderFullInfo(orderId ?? '', {
    enabled: !!orderId,
  });

  const renderContent = (info: OrderFullInfo | undefined) => {
    if (!info) {
      return null;
    }

    const {
      id,
      eventId,
      action,
      choice,
      auto_cancel,
      stop_loss,
      take_profit,
      labels,
    } = info;

    return (
      <>
        <Header title={`Details of order ${id}`} mb={2} />

        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={labels.length > 0 ? 1 : 2}
        >
          <SecondaryLabel label="Event ID:" />
          <EventLink eventId={eventId} />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={labels.length > 0 ? 1 : 2}
        >
          <SecondaryLabel label="Selected side:" />
          <StringValue value={capitalize(choice)} isPrimary={true} />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={labels.length > 0 ? 1 : 2}
        >
          <SecondaryLabel label="Action:" />
          <StringValue value={formatOrderEnumValue(action)} isPrimary={true} />
        </Box>

        {labels.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            mb={2}
          >
            <SecondaryLabel label="Labels:" />
            {labels.map(label => (
              <OrderLabelBadge key={label} label={label} />
            ))}
          </Box>
        )}

        {(auto_cancel || stop_loss || take_profit) && (
          <CardBox bgcolor={COLOR__WHITE} mt={4}>
            <Typography component="div" fontWeight={500} mb={3}>
              Modifiers
            </Typography>

            <Box display="flex" gap={4} flexWrap="wrap">
              {auto_cancel && (
                <CardBox {...cardBoxCommonProps}>
                  <Item
                    label={<PrimaryLabel label="Auto-cancel" />}
                    value={null}
                    paddingLeft={0}
                    isExpandable={false}
                    isListExpandable={false}
                  >
                    <Item
                      label={<SecondaryLabel label="Timeout" />}
                      value={
                        <StringValue
                          value={formatInTz(
                            auto_cancel.timeoutAt,
                            DATETIME_FORMAT,
                          )}
                          isPrimary={false}
                        />
                      }
                      paddingLeft={2}
                      isListExpandable={false}
                    />
                    <Item
                      label={<SecondaryLabel label="Status" />}
                      value={
                        <StringValue
                          value={auto_cancel.status}
                          isPrimary={false}
                        />
                      }
                      paddingLeft={2}
                      isListExpandable={false}
                    />
                  </Item>
                </CardBox>
              )}

              {stop_loss && (
                <CardBox {...cardBoxCommonProps}>
                  <Item
                    label={<PrimaryLabel label="Stop loss" />}
                    value={null}
                    paddingLeft={0}
                    isExpandable={false}
                    isListExpandable={false}
                  >
                    <Item
                      label={<SecondaryLabel label="Price" />}
                      value={
                        <NumberValue
                          value={stop_loss.price}
                          isPrimary={false}
                        />
                      }
                      paddingLeft={2}
                      isListExpandable={false}
                    />
                    <Item
                      label={<SecondaryLabel label="Status" />}
                      value={
                        <StringValue
                          value={capitalize(stop_loss.status)}
                          isPrimary={false}
                        />
                      }
                      paddingLeft={2}
                      isListExpandable={false}
                    />
                  </Item>
                </CardBox>
              )}

              {take_profit && (
                <CardBox {...cardBoxCommonProps}>
                  <Item
                    label={<PrimaryLabel label="Take profit" />}
                    value={null}
                    paddingLeft={0}
                    isExpandable={false}
                    isListExpandable={false}
                  >
                    <Item
                      label={<SecondaryLabel label="Price" />}
                      value={
                        <NumberValue
                          value={take_profit.price}
                          isPrimary={false}
                        />
                      }
                      paddingLeft={2}
                      isListExpandable={false}
                    />
                    <Item
                      label={<SecondaryLabel label="Status" />}
                      value={
                        <StringValue
                          value={capitalize(take_profit.status)}
                          isPrimary={false}
                        />
                      }
                      paddingLeft={2}
                      isListExpandable={false}
                    />
                  </Item>
                </CardBox>
              )}
            </Box>
          </CardBox>
        )}

        <CardBox bgcolor={COLOR__WHITE} mt={4}>
          <Tabs
            value={urlState.tab}
            onChange={(_, value: OrderTab) => setUrl({tab: value})}
            sx={{mb: 3}}
          >
            {tabs.map(({value, label}) => (
              <Tab key={value} value={value} label={label} />
            ))}
          </Tabs>

          {urlState.tab === 'items' && (
            <OrderItemsTable orderId={id} eventId={eventId} />
          )}
          {urlState.tab === 'transactions' && (
            <OrderTransactionsTab orderId={id} />
          )}
        </CardBox>
      </>
    );
  };

  return (
    <BaseWidgetLayout isPending={isPending} error={error}>
      {renderContent(data)}
    </BaseWidgetLayout>
  );
};
